/**
 * De Ceuster brand files, served from `public/brand/`.
 *
 * Every logo comes as a pair: the light-mode file and the white variant De
 * Ceuster's house style prescribes for dark (blue) surfaces. The favicon
 * (`/brand/icon.svg`, the DC icon) is set statically in index.html.
 */

export const BRAND_NAME = "De Ceuster";

/** Media query that switches a themed image to its dark-mode file. */
export const DARK_MODE_QUERY = "(prefers-color-scheme: dark)";

export interface ThemedImageSource {
  light: string;
  dark: string;
}

export const BRAND_LOGOS = {
  /** Master logo with the baseline "Bouwkranen - Grues". */
  full: { light: "/brand/logo.svg", dark: "/brand/logo-white.svg" },
  /** Without the baseline, for small sizes such as the page header. */
  compact: { light: "/brand/logo-nobase.svg", dark: "/brand/logo-nobase-white.svg" },
  /**
   * The DC mark alone (the favicon), for the collapsed sidebar: blue on a white
   * disc, which reads on light and dark surfaces alike.
   */
  mark: { light: "/brand/icon.svg", dark: "/brand/icon.svg" },
} as const satisfies Record<string, ThemedImageSource>;

export type BrandLogoVariant = keyof typeof BRAND_LOGOS;

/** A full-screen photo: the largest file as `src`, every size in `srcSet`. */
export interface BackdropPhoto {
  src: string;
  srcSet: string;
  /**
   * Tailwind object-position class (a full literal, so Tailwind generates it),
   * so a narrow (phone) crop keeps the subject in view.
   */
  focusClass: string;
}

/**
 * The photo behind the sign-in screens (`<AuthLayout>`): the card floats over it.
 * A Sky840 tower crane being assembled in Berlaar, from De Ceuster's own website
 * (deceuster.be, "sky840-ABHR-deceuster-video-thumb.jpg", March 2026), re-encoded
 * to WebP at 1920 and 1080 px wide. Null shows the split screen with the
 * line-drawn crane (`<CraneIllustration>`) instead.
 */
export const AUTH_BACKDROP: BackdropPhoto | null = {
  src: "/brand/sign-in-sky840-1920.webp",
  srcSet: "/brand/sign-in-sky840-1080.webp 1080w, /brand/sign-in-sky840-1920.webp 1920w",
  focusClass: "object-[68%_45%]",
};

/** Where the "Built by Velta" credit links to (opens in a new tab). */
export const VELTA_URL = "https://thinkvelta.ai";

/** Velta's logo, used only for the small "Built by Velta" credit. */
export const VELTA_LOGO: ThemedImageSource = {
  light: "/brand/velta.svg",
  dark: "/brand/velta-white.svg",
};
